import React, { useState, useEffect, useRef } from 'react';
import {
    LayoutDashboard, MessageSquare, Settings, Calendar, Bell, Users,
    LogOut, FileBarChart, CalendarPlus, CreditCard, Clock,
    ClipboardList, AlertTriangle, Menu, X, DollarSign, Shield, BookOpen, Loader2, Headphones
} from 'lucide-react';

// --- IMPORTS DO CAPACITOR ---
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

// --- SERVIÇOS DO SISTEMA ---
import { socket, connectSocket, disconnectSocket } from '../services/socket';
import api from '../services/api';
import Modal from '../components/Modal';

// --- TELAS DO SISTEMA ---
import Dashboard from './Dashboard';
import ChatScreen from './ChatScreen';
import ConfiguracoesScreen from './ConfiguracoesScreen';
import AgendaScreen from './AgendaScreen';
import AgendamentosScreen from './AgendamentosScreen';
import AtendimentosScreen from './AtendimentosScreen';
import MuralScreen from './MuralScreen';
import InternalChatScreen from './InternalChatScreen';
import RelatoriosContainer from './RelatoriosContainer';
import AssinaturaScreen from './AssinaturaScreen';
import OrdemServicoScreen from './OrdemServicoScreen';
import FinanceiroScreen from './FinanceiroScreen';
import NotasFiscaisScreen from './NotasFiscaisScreen'; // 🆕
import CadernoScreen from './CadernoScreen';
import SuporteScreen from './SuporteScreen';

// Helper para converter tempo (HH:MM) em minutos absolutos
const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

const isEventAllowedByClientGroups = (user, data) => {
    // Se o usuário não tem restrição de grupos, permite
    if (!user || user.role === 'admin' || user.unrestrictedClients || !user.clientGroups || user.clientGroups.length === 0) return true;
    
    // Se o usuário já está no ticket (como agente principal ou co-atendente), permite
    const myId = String(user._id || user.id);
    const isMyTicket = String(data.ticketAgentId || data.agentId) === myId || 
        (data.ticketAttendants && data.ticketAttendants.some(a => String(a?._id || a) === myId)) ||
        (data.attendants && data.attendants.some(a => String(a?._id || a) === myId));
    if (isMyTicket) return true;

    // Se o ticket tem grupos, verifica se há interseção com os grupos do usuário
    if (data.ticketClientGroups && data.ticketClientGroups.length > 0) {
        return data.ticketClientGroups.some(g => user.clientGroups.includes(String(g)));
    }
    
    // Se o ticket não tem grupos e o usuário tem grupos, NÃO permite
    return false;
};

const MainLayout = () => {
    // --- ESTADOS GLOBAIS DE NAVEGAÇÃO E USUÁRIO ---
    const [currentView, setCurrentView] = useState('dashboard');
    const [currentUser, setCurrentUser] = useState(null);
    const currentUserRef = useRef(null); // Ref para evitar stale closure nos listeners de socket
    const [companyData, setCompanyData] = useState(null);

    // 🚨 ESTADO DO BOT (ADICIONADO)
    const [botConfig, setBotConfig] = useState(null);

    // 🚨 ESTADOS PARA ALERTAS E DIRECIONAMENTOS PREFERENCIAIS
    const [preferentialAlert, setPreferentialAlert] = useState(null);
    const [preferentialReply, setPreferentialReply] = useState('');
    const [preferentialTicketToOpen, setPreferentialTicketToOpen] = useState(null);

    // --- DADOS GLOBAIS (Listas compartilhadas entre telas) ---
    const [clientsList, setClientsList] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [companiesList, setCompaniesList] = useState([]);

    // --- ESTADOS DE UI ---
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Menu Mobile
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(false); // Menu Desktop
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE BLOQUEIO (Financeiro e Horário) ---
    const [isWorkScheduleBlocked, setIsWorkScheduleBlocked] = useState(false);
    const [isSubscriptionBlocked, setIsSubscriptionBlocked] = useState(false);

    // --- CONTADORES DE NOTIFICAÇÃO ---
    const [waitingCount, setWaitingCount] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [osCount, setOsCount] = useState(0); // 🆕 Contagem de OS Abertas
    const [internalCount, setInternalCount] = useState(0); // 🆕 Contagem Interno
    const [muralCount, setMuralCount] = useState(0); // 🆕 Contagem de Avisos do Mural
    const [whatsappStatus, setWhatsappStatus] = useState('LOADING'); // 🚨 CONEXÃO WHATSAPP

    // --- CONTROLE DE ÁUDIO ---
    const notificationAudioRef = useRef(new Audio('/assets/notification.mp3')); // Som de Loop
    const messageAudioRef = useRef(new Audio('/assets/message.mp3')); // Som de Bip
    const [isMuted, setIsMuted] = useState(false); // NOVO: Estado de Mute

    // --- NOTIFICAÇÃO DE SISTEMA (SUPER ADMIN) ---
    const [systemNotice, setSystemNotice] = useState(null);

    // Configuração inicial do loop de áudio
    useEffect(() => {
        notificationAudioRef.current.loop = true;
    }, []);

    // ==============================================================================================
    // 0. CONFIGURAÇÃO DE PUSH NOTIFICATION
    // ==============================================================================================
    const setupPushNotifications = async () => {
        if (Capacitor.getPlatform() === 'web') return;

        console.log('Inicializando Push Notifications...');
        try {
            let permStatus = await PushNotifications.checkPermissions();
            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
            }
            if (permStatus.receive !== 'granted') return;

            if (Capacitor.getPlatform() === 'android') {
                await PushNotifications.createChannel({
                    id: 'push_notification_channel',
                    name: 'Notificações Gerais',
                    description: 'Avisos de novas mensagens e chamados',
                    importance: 5,
                    visibility: 1,
                    vibration: true,
                    sound: 'default'
                });
            }

            const existingDeviceToken = localStorage.getItem('device_push_token');
            if (existingDeviceToken) {
                api.post('/profile/update-push-token', {
                    pushToken: existingDeviceToken,
                    platform: Capacitor.getPlatform()
                }).catch(err => console.error('Erro ao salvar token existente:', err));
            }

            await PushNotifications.removeAllListeners();

            PushNotifications.addListener('registration', async (token) => {
                try {
                    localStorage.setItem('device_push_token', token.value);
                    await api.post('/profile/update-push-token', {
                        pushToken: token.value,
                        platform: Capacitor.getPlatform()
                    });
                } catch (error) {
                    console.error('Erro ao salvar token', error);
                }
            });

            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                fetchDashboardSummary();
            });

            PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                const data = notification.notification.data;
                if (data.chatId) {
                    setPreferentialTicketToOpen(data.chatId);
                    setCurrentView('chat');
                } else if (data.screen) {
                    setCurrentView(data.screen);
                }
            });

            await PushNotifications.register();

        } catch (error) {
            console.error('Falha ao configurar Push:', error);
        }
    };

    // ==============================================================================================
    // 1. INICIALIZAÇÃO E AUTENTICAÇÃO
    // ==============================================================================================
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');

        if (!storedUser || !token) {
            window.location.href = '/';
            return;
        }

        try {
            const parsedUser = JSON.parse(storedUser);
            setCurrentUser(parsedUser);
            currentUserRef.current = parsedUser; // Sincroniza a ref imediatamente
            fetchInitialData();
            setupPushNotifications();
        } catch (e) {
            handleLogout();
        }

        connectSocket();

        const handleSocketUpdate = async (data) => {
            const user = currentUserRef.current || currentUser;
            const myCompanyId = user?.companyId;
            if (!data || !data.companyId || !myCompanyId || String(data.companyId?._id || data.companyId) !== String(myCompanyId?._id || myCompanyId)) return;
            if (!isEventAllowedByClientGroups(user, data)) return;

            // Se o ticket mudou de status e NÃO É MAIS 'waiting', força parada do som imediatamente
            // ALTERAÇÃO: 'waiting_evaluation' também conta como saída de "waiting" para fins visuais
            if (data && data.status && data.status !== 'waiting') {
                // Diminui o contador localmente para reação instantânea
                setWaitingCount(prev => Math.max(0, prev - 1));
            }
            setTimeout(() => { fetchDashboardSummary(); }, 500);
        };

        socket.on('connect', () => {
            console.log("Socket reconectado! Atualizando dados...");
            fetchInitialData();
        });

        socket.on('chat_updated', handleSocketUpdate);
        socket.on('new_ticket_created', (data) => {
            const user = currentUserRef.current || currentUser;
            const myCompanyId = user?.companyId;
            if (data && data.companyId && myCompanyId && String(data.companyId?._id || data.companyId) === String(myCompanyId?._id || myCompanyId)) {
                if (!isEventAllowedByClientGroups(user, data)) return;
                fetchDashboardSummary();
            }
        });
        socket.on('whatsapp_status', (data) => {
            const user = currentUserRef.current || currentUser;
            const myCompanyId = user?.companyId;
            if (data && data.companyId && myCompanyId && String(data.companyId?._id || data.companyId) !== String(myCompanyId?._id || myCompanyId)) return;
            if (data && data.status) setWhatsappStatus(data.status);
        });
        socket.on('notices_updated', fetchDashboardSummary);

        // ✅ NOVO: Para o loop de áudio IMEDIATAMENTE quando um ticket é aceito/direcionado
        const handleStopWaitingSound = (data) => {
            // Verificamos o companyId para garantir que só a empresa correta para o som
            const user = currentUserRef.current || currentUser;
            const myCompanyId = user?.companyId;
            if (data && data.companyId && myCompanyId && String(data.companyId?._id || data.companyId) === String(myCompanyId?._id || myCompanyId)) {
                if (!isEventAllowedByClientGroups(user, data)) return;
                // Para o áudio imediatamente
                const audio = notificationAudioRef.current;
                audio.pause();
                audio.currentTime = 0;
                // Decrementa o contador localmente para feedback instantâneo
                setWaitingCount(prev => Math.max(0, prev - 1));
                // Confirma com API após 500ms para ter contagem exata
                setTimeout(() => fetchDashboardSummary(), 500);
            }
        };
        socket.on('stop_waiting_sound', handleStopWaitingSound);

        socket.on('new_message_notification', (data) => {
            if (!isWorkScheduleBlocked && !isSubscriptionBlocked && !isMuted) { // Respeita Mute
                try {
                    const user = currentUserRef.current || currentUser;
                    const myId = String(user?._id || user?.id);
                    const myCompanyId = user?.companyId;
                    
                    if (!data || !data.companyId || !myCompanyId || String(data.companyId?._id || data.companyId) !== String(myCompanyId?._id || myCompanyId)) return;
                    if (!isEventAllowedByClientGroups(user, data)) return;

                    const isMyTicket = data && (
                        String(data.agentId || data.ticketAgentId) === myId || 
                        (data.attendants && data.attendants.some(a => String(a?._id || a) === myId)) ||
                        (data.ticketAttendants && data.ticketAttendants.some(a => String(a?._id || a) === myId))
                    );
                    const isWaiting = data && data.status === 'waiting';

                    if (isMyTicket || isWaiting || !data) {
                        messageAudioRef.current.currentTime = 0;
                        messageAudioRef.current.play().catch(e => console.log("Áudio bloqueado:", e));
                    }
                } catch (e) { }
            }
        });

        socket.on('preferential_alert', (data) => {
            // Usa a ref para garantir o valor mais recente do currentUser (evita stale closure)
            const user = currentUserRef.current;
            const myId = user?._id || user?.id;
            console.log('[PREF ALERT] Recebido. meuId:', myId, '| agentId alvo:', data.agentId);
            if (myId && String(myId) === String(data.agentId)) {
                setPreferentialAlert(data);
                if (messageAudioRef.current) {
                    messageAudioRef.current.currentTime = 0;
                    messageAudioRef.current.play().catch(e => console.log("Áudio bloqueado:", e));
                }
            }
        });

        socket.on('force_logout', () => {
            alert("Sua sessão foi encerrada remotamente.");
            handleLogout();
        });

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log("Aba visível! Atualizando dados...");
                fetchInitialData();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleVisibilityChange);
        window.addEventListener('refresh_dashboard', fetchDashboardSummary);

        return () => {
            disconnectSocket();
            socket.off('chat_updated', handleSocketUpdate);
            socket.off('new_ticket_created', fetchDashboardSummary);
            socket.off('stop_waiting_sound', handleStopWaitingSound);
            socket.off('new_message_notification');
            socket.off('force_logout');
            socket.off('whatsapp_status');
            socket.off('notices_updated', fetchDashboardSummary);
            notificationAudioRef.current.pause();

            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleVisibilityChange);
            window.removeEventListener('refresh_dashboard', fetchDashboardSummary);

            if (Capacitor.getPlatform() !== 'web') {
                PushNotifications.removeAllListeners();
            }
        };
    }, [isMuted]); // Re-registra listener se mudar estado de mute

    // ==============================================================================================
    // 1.5. MONITORAMENTO GLOBAL DO WHATSAPP (REMOVIDO FIREBASE)
    // ==============================================================================================
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await api.get('/whatsapp/status');
                setWhatsappStatus(res.data.connectionStatus || 'DISCONNECTED');
            } catch(e) { }
        };
        fetchStatus();
    }, [currentUser]);

    // ==============================================================================================
    // 2. CONTROLE DE LOOP DE ÁUDIO
    // ==============================================================================================
    useEffect(() => {
        const audio = notificationAudioRef.current;
        const shouldPlayLoop = waitingCount > 0 && !isWorkScheduleBlocked && !isSubscriptionBlocked && !isMuted;

        if (shouldPlayLoop) {
            if (audio.paused) {
                audio.play().catch(e => console.log("Autoplay bloqueado:", e));
            }
        } else {
            audio.pause();
            audio.currentTime = 0;
        }
    }, [waitingCount, isWorkScheduleBlocked, isSubscriptionBlocked, isMuted]);

    // ==============================================================================================
    // 3. BUSCA DE DADOS (BLINDADO CONTRA ERROS)
    // ==============================================================================================

    const fetchDashboardSummary = async () => {
        try {
            const res = await api.get('/dashboard/summary');
            setWaitingCount(res.data.waitingChats || 0);
            setActiveCount(res.data.activeChats || 0);
            setOsCount(res.data.openWorkOrders || 0);
            if (res.data.noticesCount !== undefined) {
                setMuralCount(res.data.noticesCount);
            }

            // Fetch internal chat unread messages
            try {
                const internalRes = await api.get('/internal-chats/unread');
                setInternalCount(internalRes.data.unreadCount || 0);
            } catch (ignore) {}

            // Fallback para buscar contagem de avisos se não estiver no summary
            if (res.data.noticesCount === undefined) {
                try {
                    const noticesRes = await api.get('/notices');
                    if (Array.isArray(noticesRes.data)) {
                        setMuralCount(noticesRes.data.length);
                    }
                } catch (ignore) {}
            }
        } catch (error) {
            console.error("Erro ao atualizar contadores:", error);
        }
    };

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            // 🚨 CARREGAMENTO RESILIENTE: SE UM FALHAR, OS OUTROS CONTINUAM
            // Usamos .catch() individualmente para que o Promise.all não rejeite tudo

            const [companyRes, usersRes, clientsRes, companiesRes, botConfigRes, allowedClientsRes] = await Promise.all([
                api.get('/company/status').catch(() => ({ data: null })),
                api.get('/users').catch(() => ({ data: [] })),
                api.get('/clients?limit=2000').catch(() => ({ data: [] })),
                api.get('/external-companies').catch(() => ({ data: [] })),
                api.get('/settings/bot').catch(() => ({ data: {} })), // <--- Isso evita o travamento
                api.get('/users/me/allowed-clients').catch(() => ({ data: null }))
            ]);

            // Busca aviso do sistema
            try {
                const noticesRes = await api.get('/system-notices');
                const notices = noticesRes.data;
                if (notices && notices.length > 0) {
                    setSystemNotice(notices[0]);
                }
            } catch (ignore) {}

            setCompanyData(companyRes.data);
            setUsersList(usersRes.data || []);
            setBotConfig(botConfigRes.data || {});

            if (allowedClientsRes && allowedClientsRes.data) {
                const updatedUser = { ...currentUserRef.current };
                updatedUser.unrestrictedClients = allowedClientsRes.data.unrestrictedClients;
                updatedUser.allowedClientIds = allowedClientsRes.data.allowedClientIds;
                setCurrentUser(updatedUser);
                currentUserRef.current = updatedUser;
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }

            const clientsData = clientsRes.data;
            setClientsList(Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []));

            // Fix for paginated response structure
            const companiesData = companiesRes.data;
            if (companiesData && Array.isArray(companiesData.data)) {
                setCompaniesList(companiesData.data);
            } else if (Array.isArray(companiesData)) {
                setCompaniesList(companiesData);
            } else {
                setCompaniesList([]);
            }

            checkBlocks(companyRes.data);
            await fetchDashboardSummary();

        } catch (e) {
            console.error("Erro crítico no carregamento inicial:", e);
        } finally {
            setLoading(false);
        }
    };

    // 🚨 FUNÇÃO PARA SALVAR CONFIGURAÇÕES DO BOT
    const handleSaveBotConfig = async (newConfig) => {
        try {
            setBotConfig(prev => ({ ...prev, ...newConfig }));
            await api.put('/settings/bot', newConfig);
            console.log("Configurações do Bot salvas.");
        } catch (error) {
            console.error("Erro ao salvar config do bot:", error);
            alert("Erro ao salvar configuração.");
            fetchInitialData();
        }
    };

    // Monitoramento de horário
    useEffect(() => {
        const interval = setInterval(() => {
            if (companyData) checkBlocks(companyData);
        }, 60000);
        return () => clearInterval(interval);
    }, [companyData, currentUser]);

    // Lógica de Bloqueio
    const checkBlocks = (data) => {
        if (!data) return;
        let isSuspended = ['overdue', 'suspended', 'canceled'].includes(data.status);
        if (!isSuspended && data.next_due_date) {
            const dueDate = new Date(data.next_due_date);
            const now = new Date();
            const diffDays = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
            if (diffDays > 3) isSuspended = true;
        }
        setIsSubscriptionBlocked(isSuspended);

        if (data.settings?.workingHoursEnabled && currentUser?.role !== 'admin') {
            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            const dayOfWeek = now.getDay();
            const todaySchedule = data.settings.schedule?.[dayOfWeek];

            if (!todaySchedule || !todaySchedule.isOpen) {
                setIsWorkScheduleBlocked(true);
            } else {
                const start = timeToMinutes(todaySchedule.start);
                const end = timeToMinutes(todaySchedule.end);
                if (currentMinutes < start || currentMinutes > end) setIsWorkScheduleBlocked(true);
                else setIsWorkScheduleBlocked(false);
            }
        } else {
            setIsWorkScheduleBlocked(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        disconnectSocket();
        window.location.href = '/';
    };

    // ==============================================================================================
    // 3.1. REDIRECT AUTOMÁTICO SE DASHBOARD BLOQUEADO
    // ==============================================================================================
    useEffect(() => {
        if (currentView === 'dashboard' && currentUser && !currentUser.permissions?.admin) {
            if (currentUser.permissions?.block_menu_dashboard) {
                setCurrentView('chat'); // Redireciona para Chat (Tela segura)
            }
        }
    }, [currentUser, currentView, currentUser?.permissions?.block_menu_dashboard]);

    // ==============================================================================================
    // AGENDA EVENTS
    // ==============================================================================================
    const [agendaEvents, setAgendaEvents] = useState([]);

    const fetchAgendaEvents = async () => {
        try {
            const res = await api.get('/agenda');
            setAgendaEvents(res.data || []);
        } catch (e) {
            console.error('[AGENDA] Erro ao carregar eventos:', e);
        }
    };

    useEffect(() => {
        fetchAgendaEvents();
    }, []);

    const handleAddAgendaEvent = async (eventData) => {
        try {
            const res = await api.post('/agenda', eventData);
            setAgendaEvents(prev => [...prev, res.data]);
        } catch (e) {
            console.error('[AGENDA] Erro ao adicionar evento:', e);
            alert('Erro ao salvar evento.');
        }
    };

    const handleDeleteAgendaEvent = async (eventId) => {
        try {
            await api.delete(`/agenda/${eventId}`);
            setAgendaEvents(prev => prev.filter(ev => ev.id !== eventId && ev._id !== eventId));
        } catch (e) {
            console.error('[AGENDA] Erro ao deletar evento:', e);
            alert('Erro ao excluir evento.');
        }
    };
    const handleAssumePreferential = async (withReply) => {
        if (!preferentialAlert) return;
        try {
            const ticketId = preferentialAlert.ticketId;
            const myId = currentUser?._id || currentUser?.id;
            
            // 1. Assumir
            await api.put(`/chats/${ticketId}`, { status: 'open', agentId: myId });

            // 2. Enviar resposta se houver
            if (withReply && preferentialReply.trim()) {
                await api.post(`/chats/${ticketId}/send`, { body: preferentialReply.trim(), type: 'text' });
            }

            // 3. Redirecionar
            setPreferentialTicketToOpen(ticketId);
            setCurrentView('chat');
            setPreferentialAlert(null);
            setPreferentialReply('');
        } catch (e) {
             console.error(e);
             alert('Erro ao assumir atendimento preferencial.');
        }
    };

    const renderContent = () => {
        const commonProps = {
            currentUser,
            companyData,
            clients: clientsList,
            users: usersList,
            externalCompanies: companiesList,
            navigateTo: setCurrentView,
            isMuted,
            setIsMuted,
            whatsappStatus,
            ticketToOpen: preferentialTicketToOpen,
            clearTicketToOpen: () => setPreferentialTicketToOpen(null)
        };

        switch (currentView) {
            case 'dashboard': return <Dashboard {...commonProps} />;
            case 'chat': return <ChatScreen {...commonProps} />;
            case 'atendimentos': return <AtendimentosScreen {...commonProps} />;
            case 'internal': return <InternalChatScreen {...commonProps} />;
            case 'schedule': return <AgendaScreen
                {...commonProps}
                agendaEvents={agendaEvents}
                onAddEvent={handleAddAgendaEvent}
                onDeleteEvent={handleDeleteAgendaEvent}
            />;
            case 'mural': return <MuralScreen {...commonProps} />;
            case 'reports': return <RelatoriosContainer {...commonProps} />;
            case 'caderno': return <CadernoScreen {...commonProps} />;

            case 'settings': return <ConfiguracoesScreen
                {...commonProps}
                botConfig={botConfig} // Passa o estado atual
                onSaveBotConfig={handleSaveBotConfig} // Passa a função de salvar
                // Props de compatibilidade
                welcomeMessages={botConfig?.welcomeMessages || []}
                endMessage={botConfig?.endMessage}
                pauseMessage={botConfig?.pauseMessage}
                onAddWelcomeMessage={async (msg) => {
                    const newMsgs = [...(botConfig.welcomeMessages || []), { ...msg, id: Date.now() }];
                    await handleSaveBotConfig({ welcomeMessages: newMsgs });
                }}
                onDeleteWelcomeMessage={async (id) => {
                    const newMsgs = botConfig.welcomeMessages.filter(m => m.id !== id);
                    await handleSaveBotConfig({ welcomeMessages: newMsgs });
                }}
                onSaveEndMessage={async (text) => await handleSaveBotConfig({ endMessage: { isActive: !!text, text } })}
                onSavePauseMessage={async (text) => await handleSaveBotConfig({ pauseMessage: { isActive: !!text, text } })}
            />;

            case 'subscription': return <AssinaturaScreen {...commonProps} />;
            case 'os': return <OrdemServicoScreen {...commonProps} />;
            case 'scheduled-msgs': return <AgendamentosScreen {...commonProps} />;
            case 'finance': return <FinanceiroScreen {...commonProps} />;
            case 'nfse': return <NotasFiscaisScreen {...commonProps} />; // 🆕
            case 'support': return <SuporteScreen {...commonProps} />;
            default: return <Dashboard {...commonProps} />;
        }
    };

    if (!currentUser) return null;

    // ==============================================================================================
    // 3. RENDERIZAÇÃO DO MENU LATERAL (COM FILTRO DE PERMISSÕES)
    // ==============================================================================================
    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, block: 'block_menu_dashboard' },
        { id: 'chat', label: 'Conversas', icon: MessageSquare, count: waitingCount, countColor: 'bg-red-500' }, // Conversas nunca bloqueia (Core)
        { id: 'internal_chat', label: 'Chat Interno', icon: Users, block: 'block_menu_internal_chat', count: internalCount, countColor: 'bg-blue-500' },
        { id: 'monitoring', label: 'Atendimentos', icon: Users, block: 'block_menu_monitoring' },
        { id: 'mural', label: 'Mural de Avisos', icon: Bell, block: 'block_menu_notices' },
        { id: 'agenda', label: 'Agenda', icon: Calendar, block: 'block_menu_agenda' },
        { id: 'agendamentos', label: 'Agendamentos', icon: CalendarPlus, block: 'block_menu_agenda' }, // Usa mesma key
        { id: 'ordens', label: 'Ordens de Serviço', icon: ClipboardList, block: 'block_menu_work_orders' },
        { id: 'relatorios', label: 'Relatórios', icon: FileBarChart, block: 'block_reports_view' }, // Pode ter logica mais complexa
        { id: 'configuracoes', label: 'Configurações', icon: Settings }, // Configurações filtra as abas internamente
        { id: 'assinatura', label: 'Assinatura', icon: CreditCard, block: 'block_config_subscription' } // Atalho direto
    ].filter(item => {
        // Admin vê tudo
        if (currentUser?.role === 'admin' || currentUser?.permissions?.admin) return true;
        // Se tiver bloqueio explícito, remove
        if (item.block && currentUser?.permissions?.[item.block]) return false;

        // Regra especial para Relatórios (se todos os sub-relatórios estiverem bloqueados, poderia ocultar, mas por enquanto mantemos)
        return true;
    });

    let navItems = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, section: 'Geral' },
        { id: 'chat', label: 'Conversas', icon: MessageSquare, badge: waitingCount, section: 'Geral' },
        { id: 'atendimentos', label: 'Atendimentos', icon: Users, section: 'Geral' },
        { id: 'os', label: 'Ordens de Serviço', icon: ClipboardList, section: 'Geral', badge: osCount },
        { id: 'internal', label: 'Chat Interno', icon: Users, section: 'Equipe', badge: internalCount },
        { id: 'mural', label: 'Mural de Avisos', icon: Bell, section: 'Equipe', badge: muralCount },
        { id: 'schedule', label: 'Agenda', icon: Calendar, section: 'Equipe' },
        { id: 'scheduled-msgs', label: 'Agendamentos', icon: CalendarPlus, section: 'Equipe' },
        { id: 'caderno', label: 'Caderno', icon: BookOpen, section: 'Equipe' },
        { id: 'reports', label: 'Relatórios', icon: FileBarChart, section: 'Gestão' },
        { id: 'settings', label: 'Configurações', icon: Settings, section: 'Gestão' },
        { id: 'subscription', label: 'Assinatura', icon: CreditCard, section: 'Gestão' },
        { id: 'support', label: 'Canal de Suporte', icon: Headphones, section: 'Suporte' }
    ];

    if (companyData?.modules?.financeiro) {
        navItems.splice(9, 0, { id: 'finance', label: 'Gestão Financeira', icon: DollarSign, section: 'Gestão' });
    }

    if (companyData?.modules?.notas_fiscais) {
        // Find insert index dynamically if needed, or hardcode after finance
        const insertIdx = companyData?.modules?.financeiro ? 10 : 9;
        navItems.splice(insertIdx, 0, { id: 'nfse', label: 'Notas Fiscais', icon: FileBarChart, section: 'Gestão' }); // 🆕
    }

    // Role filtering for frontend - same as menuItems but applied to navItems
    navItems = navItems.filter(item => {
        // Admin sees all
        if (currentUser?.role === 'admin' || currentUser?.permissions?.admin) return true;
        // Block by specific permission if needed (Assuming similar logic to menuItems)
        const blockMapping = {
            'dashboard': 'block_menu_dashboard',
            'chat': 'block_menu_chat',
            'internal': 'block_menu_internal_chat',
            'atendimentos': 'block_menu_monitoring',
            'mural': 'block_menu_notices',
            'schedule': 'block_menu_agenda',
            'scheduled-msgs': 'block_menu_scheduled_msgs',
            'os': 'block_menu_work_orders',
            'reports': 'block_reports_view',
            'subscription': 'block_config_subscription',
            'caderno': 'block_menu_notebook'
        };
        const blockKey = blockMapping[item.id];
        if (blockKey && currentUser?.permissions?.[blockKey]) return false;

        // Hide finance / nfse for non-admins by default, or add specific permission blocks later
        // Currently relying on general admin/agent view or module presence
        return true;
    });

    const handleExitImpersonation = () => {
        const superAdminToken = localStorage.getItem('superAdminToken');
        if (superAdminToken) {
            localStorage.setItem('token', superAdminToken);
            localStorage.removeItem('superAdminToken');
            window.location.href = '/superadmin';
        }
    };

    const isImpersonating = !!localStorage.getItem('superAdminToken');

    return (
        <div className={`flex h-full w-full max-w-[100vw] fixed inset-0 bg-gray-100 overflow-hidden font-sans relative ${isImpersonating ? 'pt-10' : ''}`}>
            {isImpersonating && (
                <div className="absolute top-0 left-0 w-full h-10 bg-red-600 text-white px-4 flex justify-between items-center z-[99999] shadow-md">
                    <div className="flex items-center font-bold text-sm truncate">
                        <AlertTriangle className="w-4 h-4 mr-2 hidden sm:block" /> 👀 MODO SILENCIOSO DE SUPORTE
                    </div>
                    <button onClick={handleExitImpersonation} className="bg-white text-red-600 px-3 py-1 rounded shadow-sm text-xs font-bold hover:bg-gray-100">
                        Voltar ao Admin
                    </button>
                </div>
            )}
            {/* 🚨 MODAL DE ATENDIMENTO PREFERENCIAL 🚨 */}
            {preferentialAlert && (
                <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-in zoom-in-95">
                        <div className="flex items-center text-orange-600 mb-4">
                            <AlertTriangle className="w-8 h-8 mr-3 animate-pulse" />
                            <h2 className="text-xl font-bold">Atendimento Preferencial!</h2>
                        </div>
                        
                        <p className="text-gray-700 text-sm mb-1">Cliente na fila de espera:</p>
                        <p className="text-gray-900 font-bold text-lg mb-3">{preferentialAlert.clientName}</p>
                        
                        <div className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded text-sm text-gray-700 italic mb-4 max-h-32 overflow-y-auto custom-scrollbar">
                            "{preferentialAlert.message || 'Mensagem recebida'}"
                        </div>

                        <textarea 
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 mb-5 resize-none shadow-sm"
                            placeholder="Responda rapidamente para assumir (opcional)..."
                            rows={3}
                            value={preferentialReply}
                            onChange={e => setPreferentialReply(e.target.value)}
                        />

                        <div className="flex justify-end gap-2">
                            <button onClick={() => setPreferentialAlert(null)} className="px-4 py-2 text-gray-500 hover:bg-gray-100 font-semibold rounded-lg transition-colors">
                                Ignorar
                            </button>
                            <button onClick={() => handleAssumePreferential(false)} className="px-4 py-2 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300 transition-colors shadow-sm">
                                Assumir
                            </button>
                            <button onClick={() => handleAssumePreferential(true)} disabled={!preferentialReply.trim()} className="px-4 py-2 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                                Responder e Assumir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ALERTA GLOBAL DO WHATSAPP */}
            {(whatsappStatus === 'RECONNECTING' || whatsappStatus === 'STARTING') && (
                <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white py-2 px-4 flex justify-center items-center text-xs md:text-sm font-semibold shadow-md">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reconectando ao WhatsApp automaticamente... Por favor, aguarde.
                </div>
            )}

            {whatsappStatus === 'DISCONNECTED' && currentView !== 'settings' && (
                <div 
                    onClick={async () => {
                        setWhatsappStatus('STARTING');
                        try {
                            await api.post('/whatsapp/start');
                        } catch (_) {}
                        sessionStorage.setItem('targetSettingsTab', 'connection');
                        window.dispatchEvent(new CustomEvent('navigateSettingsTab', { detail: { tab: 'connection' } }));
                        setCurrentView('settings');
                    }} 
                    className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white p-3 flex justify-center items-center shadow-md cursor-pointer hover:bg-red-700 animate-in slide-in-from-top-4"
                >
                    <AlertTriangle className="w-5 h-5 mr-3 animate-pulse" />
                    <span className="font-bold text-sm md:text-base">Atenção! Seu WhatsApp está desconectado. Clique aqui para reconectar.</span>
                </div>
            )}

            {isSubscriptionBlocked && currentView !== 'subscription' && (
                <div className="fixed inset-0 bg-red-900 z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg text-center">
                        <AlertTriangle className="w-20 h-20 text-red-600 mx-auto mb-4" />
                        <h2 className="text-3xl font-bold text-gray-800 mb-2">Acesso Suspenso</h2>
                        <p className="text-gray-600 mb-6">Sua assinatura está pendente ou cancelada.</p>
                        <button onClick={() => { setCurrentView('subscription'); }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold w-full">Ir para Assinatura</button>
                    </div>
                </div>
            )}
            {isWorkScheduleBlocked && !isSubscriptionBlocked && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-95 z-[50] flex items-center justify-center backdrop-blur-sm">
                    <div className="bg-white p-10 rounded-2xl shadow-2xl text-center max-w-lg">
                        <Clock className="w-12 h-12 text-blue-600 mx-auto mb-6" />
                        <h2 className="text-3xl font-extrabold text-gray-800 mb-3">Expediente Encerrado</h2>
                        <p className="text-gray-500 mb-6">Seu horário de trabalho acabou. Até amanhã!</p>
                        <button onClick={handleLogout} className="bg-gray-800 text-white px-8 py-3 rounded-full hover:bg-gray-900 font-bold w-full flex items-center justify-center transition-colors">
                            <LogOut className="w-5 h-5 mr-2" /> Sair do Sistema
                        </button>
                    </div>
                </div>
            )}

            <aside className={`bg-gray-900 text-white flex-shrink-0 hidden md:flex flex-col shadow-xl z-20 transition-all duration-300 ${isMenuCollapsed ? 'w-20' : 'w-64'}`}>
                <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800 bg-gray-900">
                    {!isMenuCollapsed && (
                        <div className="flex items-center font-bold text-xl tracking-tight">
                            <MessageSquare className="w-6 h-6 mr-2 text-blue-500" />
                            <span>WA-Desk</span>
                        </div>
                    )}
                    <button onClick={() => setIsMenuCollapsed(!isMenuCollapsed)} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg mx-auto">
                        <Menu className="w-6 h-6" />
                    </button>
                </div>

                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
                    {['Geral', 'Equipe', 'Gestão', 'Suporte'].map(section => {
                        const items = navItems.filter(i => i.section === section);
                        if (items.length === 0) return null;
                        return (
                            <div key={section} className="mb-4">
                                {!isMenuCollapsed && <p className="px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 mt-2">{section}</p>}
                                {items.map(item => (
                                    <NavButton
                                        key={item.id}
                                        view={item.id}
                                        current={currentView}
                                        setView={setCurrentView}
                                        icon={item.icon}
                                        label={item.label}
                                        collapsed={isMenuCollapsed}
                                        badge={item.badge}
                                        disabled={isSubscriptionBlocked && item.id !== 'subscription'}
                                    />
                                ))}
                            </div>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-gray-800 bg-gray-900">
                    <button onClick={handleLogout} className={`flex items-center text-red-400 hover:bg-red-900/20 hover:text-red-300 w-full py-2 rounded-lg transition-all text-sm font-medium ${isMenuCollapsed ? 'justify-center' : 'justify-start px-4'}`}>
                        <LogOut className={`w-5 h-5 ${!isMenuCollapsed && 'mr-2'}`} />
                        {!isMenuCollapsed && "Sair"}
                    </button>
                </div>
            </aside>

            {isSidebarOpen && (
                <div className="fixed inset-0 z-50 md:hidden flex">
                    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)}></div>
                    <aside className="bg-gray-900 text-white w-72 flex flex-col relative z-10 h-full shadow-2xl">
                        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
                            {navItems.map(item => (
                                <NavButton
                                    key={item.id}
                                    view={item.id}
                                    current={currentView}
                                    setView={(v) => { setCurrentView(v); setIsSidebarOpen(false); }}
                                    icon={item.icon}
                                    label={item.label}
                                    badge={item.badge}
                                    disabled={isSubscriptionBlocked && item.id !== 'subscription'}
                                />
                            ))}
                        </nav>
                        <div className="p-4 border-t border-gray-800 bg-gray-900">
                            <button onClick={handleLogout} className="flex items-center text-red-400 hover:bg-red-900/20 hover:text-red-300 w-full py-2 rounded-lg transition-all text-sm font-medium justify-start px-4">
                                <LogOut className="w-5 h-5 mr-2" />
                                Sair
                            </button>
                        </div>
                    </aside>
                </div>
            )}

            <main className="flex-1 min-w-0 max-w-full flex flex-col relative overflow-hidden h-full">
                <header className="md:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between shadow-sm z-10">
                    <button onClick={() => setIsSidebarOpen(true)} className="text-gray-600 p-3 -ml-2 hover:bg-gray-100 rounded-xl flex items-center justify-center transition-colors">
                        <Menu className="w-8 h-8" />
                    </button>
                    <span className="font-bold text-gray-800">WA-Desk Local</span>
                    <div className="w-8"></div>
                </header>

                <div className="flex-1 min-h-0 min-w-0 max-w-full overflow-x-hidden overflow-y-auto bg-gray-50 relative p-0">
                    {renderContent()}
                </div>
            </main>

            {/* AVISO DO SISTEMA (SUPER ADMIN) */}
            {systemNotice && (
                <Modal 
                    isOpen={!!systemNotice} 
                    onClose={async () => {
                        try { await api.post(`/system-notices/${systemNotice._id || systemNotice.id}/read`); } catch(e) {}
                        setSystemNotice(null);
                    }} 
                    title={systemNotice.title} 
                    confirmText="Entendi" 
                    onConfirm={async () => {
                        try { await api.post(`/system-notices/${systemNotice._id || systemNotice.id}/read`); } catch(e) {}
                        setSystemNotice(null);
                    }}
                >
                    <div className="space-y-4">
                        {systemNotice.imageUrl && (
                            <img src={systemNotice.imageUrl} alt="Aviso" className="w-full rounded-lg object-contain max-h-64 mb-4 shadow-sm border border-gray-200" />
                        )}
                        <p className="text-gray-800 whitespace-pre-line text-sm md:text-base leading-relaxed">{systemNotice.message}</p>
                    </div>
                </Modal>
            )}
        </div>
    );
};

const NavButton = ({ view, current, setView, icon: Icon, label, collapsed, badge, disabled }) => {
    const isActive = current === view;
    if (disabled) return null;

    return (
        <button
            onClick={() => setView(view)}
            className={`flex items-center w-full py-2.5 mb-1 rounded-lg transition-all duration-200 group relative overflow-hidden
                ${isActive ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}
                ${collapsed ? 'justify-center px-0' : 'px-3'}
            `}
            title={collapsed ? label : ''}
        >
            <div className="relative">
                <Icon className={`w-5 h-5 ${!collapsed && 'mr-3'} ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'} transition-colors`} />
                {collapsed && badge > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-gray-900"></span>}
            </div>

            {!collapsed && (
                <>
                    <span className="text-sm font-medium z-10 flex-1 text-left">{label}</span>
                    {badge > 0 && (
                        <span className="ml-auto bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                            {badge}
                        </span>
                    )}
                </>
            )}
        </button>
    );
};

export default MainLayout;